// Story 5.6 — TanStack Query READ hook for GET
// /api/v1/marketplace/me/share-listings.
//
// Powers the "Share listings" section of MyListingsPage. Owner-filtered
// by session (no user_id param — backend reads session.user_id);
// optional status filter against the 4-value enum.
//
// queryKey: ["my-listings", "share", { page, per_page, status }]
// staleTime: 30_000 (30 seconds)

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type MyListingStatus = "active" | "sold" | "cancelled" | "expired";

export interface MyShareListingsParams {
  page: number;
  per_page: number;
  /** Optional. `undefined` = no filter ("All statuses"). */
  status?: MyListingStatus;
}

export interface MyShareListingShareData {
  share_id: string;
  creator_user_id: string;
  creator_handle: string | null;
  creator_display_name: string;
  creator_image_signed_url: string | null;
  tier: string;
  edition_number: number;
  share_serial: number;
  drop_id: string;
  drop_price_cents: number;
  snapshot_image_signed_url: string | null;
}

export interface MyShareListingRow {
  listing_id: string;
  status: string;
  price_cents: number;
  seller_anonymous: boolean;
  listed_at: string;
  expires_at: string | null;
  cancelled_at: string | null;
  sold_at: string | null;
  sold_to_user_id: string | null;
  /** Whether this listing accepts below-asking offers (Story 5.10). */
  allow_bidding: boolean;
  share: MyShareListingShareData;
}

export interface MyShareListingsPageInfo {
  total_in_page: number;
  page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface MyShareListingsResponseData {
  data: MyShareListingRow[];
  page_info: MyShareListingsPageInfo;
}

async function fetchMyShareListings(
  params: MyShareListingsParams,
): Promise<MyShareListingsResponseData> {
  const search = new URLSearchParams();
  search.set("page", String(params.page));
  search.set("per_page", String(params.per_page));
  if (params.status) {
    search.set("status", params.status);
  }
  const response = await fetch(
    `/api/v1/marketplace/me/share-listings?${search.toString()}`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(
      `Failed to load your share listings (HTTP ${response.status})`,
    );
  }
  const body = (await response.json()) as { data: MyShareListingsResponseData };
  return body.data;
}

export function useMyShareListings(
  params: MyShareListingsParams,
): UseQueryResult<MyShareListingsResponseData, Error> {
  return useQuery<MyShareListingsResponseData, Error>({
    queryKey: [
      "my-listings",
      "share",
      { page: params.page, per_page: params.per_page, status: params.status ?? null },
    ],
    queryFn: () => fetchMyShareListings(params),
    staleTime: 30_000,
  });
}
